import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { LEADERBOARD } from '@/constants/testIds';
import { trackPageView } from '@/lib/attribution';
import { Award, Crown, Medal, Trophy } from 'lucide-react';

const TIER_COLOR = {
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold:   '#FFD700',
};

const RANK_ICON = { 1: Crown, 2: Trophy, 3: Medal };

export const LeaderboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView('leaderboard');
    (async () => {
      try {
        const { data } = await api.get('/leaderboard');
        setData(data);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid={LEADERBOARD.container} className="max-w-4xl mx-auto px-6 md:px-8 pt-12 pb-24">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
        // OPERATOR_LEADERBOARD · ANON_CALLSIGNS_ONLY
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-4">
        Leaderboard<span className="caelix-caret"></span>
      </h1>
      <p className="text-xs text-gray-500 max-w-2xl mb-8 leading-relaxed">
        &gt; Top operators by paid referrals. Identities are masked — every operator
        is assigned a deterministic anonymous callsign. Real names and emails are
        <span className="text-[#38BDF8]"> never</span> exposed.
      </p>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="c-card">
          <div className="c-label">TOTAL_OPERATORS</div>
          <div
            data-testid={LEADERBOARD.totalOperators}
            className="text-3xl font-bold text-white"
          >
            {String(data?.total_operators ?? 0).padStart(3, '0')}
          </div>
        </div>
        <div className="c-card">
          <div className="c-label">TOTAL_PAID_REFERRALS</div>
          <div
            data-testid={LEADERBOARD.totalReferrals}
            className="text-3xl font-bold text-[#38BDF8]"
          >
            {String(data?.total_paid_referrals ?? 0).padStart(3, '0')}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="c-card text-xs text-gray-500 tracking-widest uppercase">
          LOADING<span className="caelix-dots"></span>
        </div>
      ) : !data || data.entries.length === 0 ? (
        <div data-testid={LEADERBOARD.emptyState} className="c-card">
          <div className="text-xs text-gray-500 tracking-widest uppercase mb-3">
            &gt; no operators on the board yet
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Be the first. Every paid referral moves you up. Bronze at 3, Silver at 5, Gold at 10.
          </p>
          <div className="mt-4">
            <Link to="/signup" className="c-btn-primary text-xs">CREATE_OPERATOR</Link>
          </div>
        </div>
      ) : (
        <div className="c-card">
          <div className="border-b border-[#3F3F46] pb-3 mb-4 flex items-center justify-between">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">
              // TOP_{data.entries.length}
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-gray-600">
              GEN :: {data.generated_at.slice(0, 19).replace('T', ' ')}
            </div>
          </div>
          <div className="grid grid-cols-[60px_1fr_120px_120px_120px] gap-3 items-center text-[10px] tracking-[0.2em] uppercase text-gray-500 py-2 border-b border-[#3F3F46]">
            <div>RANK</div>
            <div>CALLSIGN</div>
            <div>TIER</div>
            <div>PAID_REFS</div>
            <div>JOINED</div>
          </div>
          {data.entries.map((e) => {
            const RankIcon = RANK_ICON[e.rank];
            const tierColor = e.milestone_tier ? TIER_COLOR[e.milestone_tier] : undefined;
            return (
              <div
                key={`${e.rank}-${e.callsign}`}
                data-testid={LEADERBOARD.row}
                className="grid grid-cols-[60px_1fr_120px_120px_120px] gap-3 items-center py-3 border-b border-[#27272A] last:border-0 text-xs hover:bg-[#27272A] transition-colors"
              >
                <div className="flex items-center gap-2">
                  {RankIcon ? (
                    <RankIcon
                      className="w-4 h-4"
                      strokeWidth={2.5}
                      style={{ color: e.rank === 1 ? '#FFD700' : e.rank === 2 ? '#C0C0C0' : '#CD7F32' }}
                    />
                  ) : (
                    <span className="text-gray-600 font-mono">#</span>
                  )}
                  <span className="text-white font-mono font-bold">{String(e.rank).padStart(2, '0')}</span>
                </div>
                <div className="font-mono text-white font-bold tracking-widest">
                  {e.callsign}
                </div>
                <div>
                  {e.milestone_tier ? (
                    <span
                      className="c-badge c-badge--active inline-flex items-center gap-1"
                      style={{ color: tierColor, borderColor: `${tierColor}55` }}
                    >
                      <Award className="w-2.5 h-2.5" strokeWidth={3} style={{ color: tierColor }} />
                      {e.milestone_tier.toUpperCase()}
                    </span>
                  ) : (
                    <span className="c-badge">—</span>
                  )}
                </div>
                <div className="font-mono text-[#38BDF8] text-lg font-bold">
                  {String(e.paid_referrals).padStart(2, '0')}
                </div>
                <div className="font-mono text-gray-500 text-[10px]">
                  {e.joined_month}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 text-[10px] text-gray-600 tracking-widest uppercase text-center">
        &gt; want on the board? create an operator, upgrade, and share your referral link
      </div>
    </div>
  );
};

export default LeaderboardPage;
